import React, { useMemo } from 'react'

import { Controller } from 'react-hook-form'

import { Textarea, Toggle } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object, arrayOf, shape, string, oneOfType, number, func } from 'prop-types'

import { useRole } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import useUseCaseTranslate from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseTranslate'
import CustomSelect from 'Pods/Shared/CustomSelect/CustomSelect'
import useFileProcessor from 'Pods/Shared/Hooks/useFileProcessor'

import {
  DESCRIPTION_MAX_LENGTH,
  TECHNOLOGY_OPTIONS,
  MATERIAL_OPTIONS,
  PRINT_RESULT_OPTIONS,
  PRINT_TIME_OPTIONS,
  translateOptions,
} from '../../../../Utils/Data'
import FileUploadSection from '../../../UseCaseFormSection/Components/FileUploadSection/FileUploadSection'

function EditModeForm({
  control,
  availablePrinterTypes,
  isFetchingPrinterTypes,
  useCase,
  mediaChanges,
  onAddMedia,
  onRemoveExistingMedia,
  onRemoveNewMedia,
  onShowNotification,
}) {
  const role = useRole()
  const { t, tv } = useUseCaseTranslate()
  const materialOptions = useMemo(() => translateOptions(MATERIAL_OPTIONS, 'material', t), [t])
  const printResultOptions = useMemo(() => translateOptions(PRINT_RESULT_OPTIONS, 'printResult', t), [t])
  const technologyOptions = useMemo(() => translateOptions(TECHNOLOGY_OPTIONS, 'technology', t), [t])
  const printTimeOptions = useMemo(() => translateOptions(PRINT_TIME_OPTIONS, 'printTime', t), [t])
  const { processFiles, isProcessing, progress } = useFileProcessor()

  const handleFilesAdded = (files) => {
    processFiles(
      files,
      () => {},
      (processedFiles) => {
        processedFiles.forEach(file => onAddMedia(file))
      },
      (errorMessage, fileName) => {
        onShowNotification(
          t('errors.processingFile', { name: fileName || '', error: errorMessage }),
          'error'
        )
      }
    )
  }

  const handleEmbedAdded = (embed) => {
    onAddMedia(embed)
  }

  const existingEmbeds = [
    ...useCase.useCaseEmbeds.filter(embed => !mediaChanges.removed.includes(embed.id)),
    ...mediaChanges.added,
  ]

  return (
    <div className="use-case-media-card__edit">
      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          {t('form.printerEditLabel')}
        </span>
        <Controller
          name="printerType"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={availablePrinterTypes}
              placeholder={t('form.printerPlaceholder')}
              disabled={
                isFetchingPrinterTypes ||
                availablePrinterTypes.length === 0
              }
              isLoading={isFetchingPrinterTypes}
              className="use-cases-area__select"
              searchable
            />
          )}
        />
      </div>

      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          {t('form.materialLabel')}
        </span>
        <Controller
          name="material"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={materialOptions}
              placeholder={t('form.materialPlaceholder')}
              className="use-cases-area__select"
              isClearable
            />
          )}
        />
      </div>

      {role !== 'seller' && (
        <div className="use-cases-area__select-container">
          <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
            {t('form.printResultLabel')}
          </span>
          <Controller
            name="printSuccess"
            control={control}
            render={({ field }) => (
              <CustomSelect
                {...field}
                options={printResultOptions}
                placeholder={t('form.printResultPlaceholder')}
                className="use-cases-area__select"
                isClearable={false}
              />
            )}
          />
        </div>
      )}

      <div className="use-cases-area__description-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">{tv('form.descriptionLabel')}</span>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              id="use-case-description-edit"
              placeholder={tv('form.descriptionPlaceholder')}
              maxLength={DESCRIPTION_MAX_LENGTH}
              textareaClassName="use-cases-area__textarea"
              isPilot
              size="small"
            />
          )}
        />
      </div>

      {/* Media Upload Section */}
      <div className="use-cases-area__file-upload use-cases-area__file-upload--edit-mode">
        <FileUploadSection
          existingMedia={existingEmbeds}
          onRemoveExisting={onRemoveExistingMedia}
          pendingMedia={[]}
          onRemoveMedia={onRemoveNewMedia}
          onFilesAdded={handleFilesAdded}
          onEmbedAdded={handleEmbedAdded}
          onShowNotification={onShowNotification}
          isProcessing={isProcessing}
          progress={progress}
          useCases={[]}
          enableDragAndDrop={false}
        />

        {existingEmbeds.length > 0 && (
          <div className="use-cases-area__media-count">
            {t('form.mediaCount', { count: existingEmbeds.length, max: 5 })}
          </div>
        )}
      </div>

      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          {t('form.technologyLabel')}
        </span>
        <Controller
          name="technology"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={technologyOptions}
              placeholder={t('form.technologyPlaceholder')}
              className="use-cases-area__select"
              isClearable
            />
          )}
        />
      </div>

      <div className="use-cases-area__select-container">
        <span className="use-cases-area__label cgt-body-base-semibold cgt-mb-10">
          {t('form.printTimeLabel')}
        </span>
        <Controller
          name="printTime"
          control={control}
          render={({ field }) => (
            <CustomSelect
              {...field}
              options={printTimeOptions}
              placeholder={t('form.printTimePlaceholder')}
              className="use-cases-area__select"
              isClearable
            />
          )}
        />
      </div>

      <div className="use-cases-area__toggle-container">
        <span className="use-cases-area__label cgt-body-base-semibold">
          {t('form.supportsUsed')}
        </span>
        <Controller
          name="usedSupport"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Toggle
              id="supports-used-toggle-edit"
              isChecked={value}
              onChange={(e, checked) => onChange(checked)}
            />
          )}
        />
      </div>
    </div>
  )
}

EditModeForm.propTypes = {
  control: object.isRequired,
  availablePrinterTypes: arrayOf(shape({
    label: string.isRequired,
    value: oneOfType([number, string]).isRequired,
    printerTypeId: oneOfType([number, string]),
    printerTypeName: string,
    technology: string,
  })).isRequired,
  isFetchingPrinterTypes: bool.isRequired,
  useCase: shape({
    useCaseEmbeds: arrayOf(shape({
      id: string.isRequired,
      url: string,
      thumbUrl: string,
      mediumUrl: string,
      source: string.isRequired,
    })),
  }).isRequired,
  mediaChanges: shape({
    added: arrayOf(object),
    removed: arrayOf(string),
  }).isRequired,
  onAddMedia: func.isRequired,
  onRemoveExistingMedia: func.isRequired,
  onRemoveNewMedia: func.isRequired,
  onShowNotification: func.isRequired,
}

export default EditModeForm
